
import * as React from 'react';
import {
    createAddon,
    IAddon,
    ISingleOperationPayload,
    IOperationHandlerThunkAction,
    registerOperationHandler,
    createSingleItemOperationHandler
} from '../../addons/registerOperationHandler';
import { IItemCacheDispatch, IItemCacheThunkAction } from '../../store/actions/ItemCacheActions';
import { registerControlHandler, createControlHandler } from '../../addons/registerControlHandler';
import { progressControl } from '../../controls/progressControl';
import { IItemCache } from '../../models/IItemCacheObserver';
import { getLocalizedCountValue, format, formatToArray } from '@ms/utilities-strings/lib/index';
import UploadStrings from '../../components/uploader/UploadStrings.resx';
import ProgressStrings from '../../components/progress/ProgressStrings.resx';
import { getItemsOperation } from '../../operations/getItems';
import DriveSpaceHelper from '@ms/items-view-utilities/lib/string/DriveSpaceHelper';
import {
    createCancelProgressItemsOperationHandler,
    getOverallSimpleProgressBreakdown,
    getOverallProgressPhase
} from '../../operations/progress';
import {
    ProgressPhase,
    sourceItemFacet,
    progressSizeFacet,
    createdItemFacet,
    destinationItemFacet
} from '../../facets/ProgressFacets';
import { folderFacet, fileSizeFacet } from '../../facets/ItemFacets';
import { entryBatchFacet } from '../../facets/FileSystemFacets';
import { readItems } from '../readItems';
import { childItemsFacet } from '../../facets/SetFacets';
import { ItemLink } from '../../components/itemLink/ItemLink';
import { cancelProgressItemsOperation } from '../../operations/progress/cancelProgressItems';
import { retryProgressCommand } from '../../components/progress/commands/retry/retryProgressCommand';
import { retryProgressItemsOperation } from '../progress/retryProgressItems';
import { replaceProgressCommand } from '../../components/progress/commands/replace/replaceProgressCommand';
import { renameProgressCommand } from '../../components/progress/commands/rename/renameProgressCommand';
import { resolveProgressItemsOperation } from '../progress/resolveProgressItems';
import { ICommand } from '../../models/commands/ICommand';
import { unpack } from '../../models/Facet';
import { conflictErrorFacet, retryErrorFacet } from '../../facets/ErrorFacets';
import { errorFacet } from '../../facets/DemandFacets';
import { UploadItemOperationError } from '../uploadItems';
import { uploadProgressKeyFacet, getUploadProgressKey } from './UploadProgressKeyFacet';
import { uploadEntryBatch } from './uploadEntries';

export { uploadProgressKeyFacet };
export { getUploadProgressKey };

interface ISummaryFormatterProps {
    count: number;
}

interface IGroupFormatterProps {
    count: number;
    itemKey: string;
}

const summaryFormatters = {
    [ProgressPhase.started]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadStartedItemsTitle,
        UploadStrings.uploadCompletedItemsTitleIntervals,
        count) || '',
        count),
    [ProgressPhase.completed]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadCompletedItemsTitle,
        UploadStrings.uploadCompletedItemsTitleIntervals,
        count) || '',
        count),
    [ProgressPhase.failed]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadFailedItemsTitle,
        UploadStrings.uploadFailedItemsTitleIntervals,
        count) || '',
        count),
    [ProgressPhase.canceled]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadFailedItemsTitle,
        UploadStrings.uploadFailedItemsTitleIntervals,
        count) || '',
        count),
};

const groupFormatters = {
    [ProgressPhase.started]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadStartedItemsDestinationTitle,
        UploadStrings.uploadCompletedItemsDestinationTitleIntervals,
        count) || '',
        count,
        <ItemLink key='link' itemKey={ itemKey } />),
    [ProgressPhase.completed]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadCompletedItemsDestinationTitle,
        UploadStrings.uploadCompletedItemsDestinationTitleIntervals,
        count) || '',
        count,
        <ItemLink key='link' itemKey={ itemKey } />),
    [ProgressPhase.failed]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadFailedItemsDestinationTitle,
        UploadStrings.uploadFailedItemsDestinationTitleIntervals,
        count) || '',
        count,
        <ItemLink key='link' itemKey={ itemKey } />),
    [ProgressPhase.canceled]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
        UploadStrings.uploadFailedItemsDestinationTitle,
        UploadStrings.uploadFailedItemsDestinationTitleIntervals,
        count) || '',
        count,
        <ItemLink key='link' itemKey={ itemKey } />),
};

/**
 * Addon to be registered by the Uploader component which manages the Progress items for uploads.
 */
export const uploadProgressAddon: IAddon = createAddon(() => {
    return (dispatch: IItemCacheDispatch) => {
        // Register an empty getItems handler to keep the queue empty.
        dispatch(registerOperationHandler(uploadProgressKeyFacet, getItemsOperation)(
            createSingleItemOperationHandler()(getUploadProgressItem)
        ));

        dispatch(registerControlHandler(uploadProgressKeyFacet, progressControl)(
            createControlHandler()(getUploadProgressControl)
        ));

        dispatch(registerOperationHandler(uploadProgressKeyFacet, cancelProgressItemsOperation)(
            createCancelProgressItemsOperationHandler(uploadProgressKeyFacet)
        ));

        dispatch(registerOperationHandler(uploadProgressKeyFacet, retryProgressItemsOperation)(
            createSingleItemOperationHandler()(retryUploadProgressItem)
        ));

        dispatch(registerOperationHandler(uploadProgressKeyFacet, resolveProgressItemsOperation)(
            createSingleItemOperationHandler()(resolveUploadProgressItem)
        ));
    };
});

/**
 * Gets the progress control for upload, which emits the strings representing the state of an upload.
 */
function getUploadProgressControl(): IItemCacheThunkAction<typeof progressControl.type> {
    return (dispatch: IItemCacheDispatch) => {
        return getUploadProgressOverrides;
    };
}

function getUploadProgressOverrides(itemCache: IItemCache, props: typeof progressControl.inputType): typeof progressControl.returnType {
    const sourceItem = itemCache.demandItemFacet(sourceItemFacet, props.itemKey);
    const entryBatch = itemCache.demandItemFacet(entryBatchFacet, sourceItem && sourceItem.itemKey);

    const totalProgressSize = getTotalProgressSize(itemCache, props);
    const percentComplete = totalProgressSize ?
        totalProgressSize.total > 0 ?
            totalProgressSize.current / totalProgressSize.total :
            1 :
        undefined;

    if (sourceItem && !entryBatch) {
        // This is a single item

        const phase = getOverallProgressPhase(itemCache, props);

        const folder = itemCache.demandItemFacet(folderFacet, sourceItem.itemKey);

        let description: string | undefined;

        if (phase === ProgressPhase.completed && totalProgressSize) {
            description = DriveSpaceHelper.getDisplayString(totalProgressSize.total);
        } else if (folder) {
            if (phase === ProgressPhase.created || phase === ProgressPhase.started) {
                description = getByteProgressDescription(totalProgressSize) || ProgressStrings.genericStartedIndeterminateDescription;
            }
        } else {
            if (phase === ProgressPhase.started) {
                description = getByteProgressDescription(totalProgressSize);
            } else if (phase === ProgressPhase.created) {
                description = ProgressStrings.genericCreatedDescription;
            }
        }

        const error = itemCache.demandItemFacet(errorFacet, props.itemKey);

        const commands: ICommand[] = [];

        if (error) {
            if (error instanceof UploadItemOperationError) {
                const {
                    conflict,
                    retry
                } = unpack({
                    conflict: conflictErrorFacet,
                    retry: retryErrorFacet
                }, error.facets);

                if (conflict) {
                    description = UploadStrings.conflictUploadWithoutRenameErrorText;

                    commands.push(replaceProgressCommand);

                    if (conflict.supportsRename) {
                        description = UploadStrings.conflictUploadErrorText;

                        commands.push(renameProgressCommand);
                    }
                } else if (retry) {
                    description = UploadStrings.generalUploadErrorText;

                    commands.push(retryProgressCommand);
                } else {
                    description = UploadStrings.generalUploadNoRetryErrorText;
                }
            } else {
                description = UploadStrings.generalUploadErrorText;

                commands.push(retryProgressCommand);
            }
        }

        return {
            percentComplete: percentComplete,
            ...(description ? {
                description
            } : {}),
            commands: commands
        };
    } else {
        // This is the batch group.
        const {
            phase,
            count
        } = getOverallSimpleProgressBreakdown(itemCache, props);

        let description: string | undefined;

        if (phase === ProgressPhase.completed && totalProgressSize) {
            description = DriveSpaceHelper.getDisplayString(totalProgressSize.total);
        } else if (phase === ProgressPhase.started) {
            description = getByteProgressDescription(totalProgressSize) || ProgressStrings.genericStartedIndeterminateDescription;
        }

        const destinationItem = itemCache.demandItemFacet(destinationItemFacet, props.itemKey);
        const destinationItemKey = destinationItem && destinationItem.itemKey;

        let title: React.ReactNode;

        if (props.format === 'group' && destinationItemKey) {
            title = groupFormatters[phase]({ count, itemKey: destinationItemKey });
        } else {
            title = summaryFormatters[phase]({ count });
        }

        return {
            ...(title ? {
                title
            } : {}),
            ...(description ? {
                description
            } : {}),
            percentComplete: percentComplete
        };
    }
}

function getTotalProgressSize(itemCache: IItemCache, props: {
    itemKey: string;
}): typeof progressSizeFacet.type | undefined {
    const progressSize = itemCache.demandItemFacet(progressSizeFacet, props.itemKey);

    if (progressSize) {
        return progressSize;
    }

    const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);

    if (childItems) {
        const totalProgressSize: typeof progressSizeFacet.type = {
            total: 0,
            current: 0
        };

        for (const childItemKey of childItems.itemKeys) {
            const childProgressSize = getTotalProgressSize(itemCache, {
                itemKey: childItemKey
            });

            if (!childProgressSize) {
                return undefined;
            }

            totalProgressSize.total += childProgressSize.total;
            totalProgressSize.current += childProgressSize.current;
        }

        return totalProgressSize;
    }

    const sourceItem = itemCache.demandItemFacet(sourceItemFacet, props.itemKey);

    if (sourceItem) {
        const fileSize = itemCache.demandItemFacet(fileSizeFacet, sourceItem.itemKey);

        if (typeof fileSize !== 'number') {
            return undefined;
        }

        const createdItem = itemCache.demandItemFacet(createdItemFacet, props.itemKey);

        return {
            total: fileSize,
            current: createdItem ? fileSize : 0
        };
    }

    return undefined;
}

function getUploadProgressItem(
    payload: ISingleOperationPayload<typeof uploadProgressKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
    return async (dispatch: IItemCacheDispatch) => {
        // Do nothing.
    };
}

function retryUploadProgressItem(
    payload: ISingleOperationPayload<typeof uploadProgressKeyFacet.type, typeof retryProgressItemsOperation.type>
): IOperationHandlerThunkAction {
    const {
        itemKey
    } = payload;

    return async (dispatch: IItemCacheDispatch) => {
        const {
            sourceItem,
            destinationItem
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                sourceItem: itemCache.demandItemFacet(sourceItemFacet, itemKey),
                destinationItem: itemCache.demandItemFacet(destinationItemFacet, itemKey)
            };
        }));

        const sourceEntryKey = sourceItem && sourceItem.itemKey;
        const parentItemKey = destinationItem && destinationItem.itemKey;

        if (!sourceEntryKey) {
            throw new Error('No source entry to retry.');
        }

        if (!parentItemKey) {
            throw new Error('No parent item to retry.');
        }

        await dispatch(uploadEntryBatch({
            sourceEntryKey,
            parentItemKey: parentItemKey
        }));
    };
}

function resolveUploadProgressItem(
    payload: ISingleOperationPayload<typeof uploadProgressKeyFacet.type, typeof resolveProgressItemsOperation.type>
): IOperationHandlerThunkAction {
    const {
        itemKey,
        options
    } = payload;

    return async (dispatch: IItemCacheDispatch) => {
        const {
            sourceItem,
            destinationItem
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                sourceItem: itemCache.demandItemFacet(sourceItemFacet, itemKey),
                destinationItem: itemCache.demandItemFacet(destinationItemFacet, itemKey)
            };
        }));

        const sourceEntryKey = sourceItem && sourceItem.itemKey;
        const parentItemKey = destinationItem && destinationItem.itemKey;

        if (!sourceEntryKey) {
            throw new Error('No source entry to retry.');
        }

        if (!parentItemKey) {
            throw new Error('No parent item to retry.');
        }

        await dispatch(uploadEntryBatch({
            sourceEntryKey,
            parentItemKey: parentItemKey,
            options
        }));
    };
}

function getByteProgressDescription(totalProgressSize: typeof progressSizeFacet.type | undefined): string | undefined {
    return totalProgressSize && totalProgressSize.total > 0 ?
        format(
            ProgressStrings.genericStartedByteProgressDescription,
            DriveSpaceHelper.getDisplayString(totalProgressSize.current),
            DriveSpaceHelper.getDisplayString(totalProgressSize.total)) :
        undefined;
}
